// @flow

import React, { Fragment } from 'react';

import { LegacyButton, Spacer } from '@zapier/design-system';
import { useMachine } from '@xstate/react';

import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import useTrackPath from 'app/developer-v3/components/PartnerSupportForm/hooks/useTrackPath';
import { AutoScroll } from 'app/developer-v3/components/PartnerSupportForm/components/AutoScroll';
import { useFormContext } from 'app/developer-v3/components/PartnerSupportForm/contexts/FormContext';
import {
  CommunityStep,
  HelpBlock,
  Interstitial,
  Separator,
  Upgrade,
} from 'app/developer-v3/teams/team-technical-support-operations/get-help';

import Contact from './Contact';
import Docs from './Docs';
import Submitted from './Submitted';
import helpMachine from './machine';
import { PartnerAdditionalHelp } from '../Topic/AdditionalHelp/PartnerAdditionalHelp';

const Help = (props /*: any */) => {
  const {
    actions: { unconfirm, answer, setMeta, submit },
    community,
    docs,
    pathId,
    canContinue,
    contact,
    data,
    state,
    skipInterstitial,
  } = props;
  const trackPath = useTrackPath();
  const {
    size,
    zap,
    isEmailSupportEligible,
    isPremierSupportEligible,
  } = useFormContext();

  const [
    {
      context: {
        hasDocs,
        hasCommunity,
        hasContact,
        dotIndex,
        dotTooltips,
        currentStep: { key, title, hasFeedback },
      },
    },
    send,
  ] = useMachine(helpMachine, {
    context: {
      hasDocs: !!docs,
      hasCommunity: !!community,
      hasContact: !!contact,
      isEmailSupportEligible,
      unconfirm,
      trackPath: path => trackPath(pathId, path),
      skipInterstitial,
    },
  });

  /*
    Scroll to the top of the content whenever:
    * The user goes to a different step in the help flow
    * The contact form has been successfully submitted
  */
  const scrollKey = `${key} ${state === 'submitted' ? 'true' : 'false'}`;

  const helpStep = children => (
    <AutoScroll scrollKey={scrollKey}>
      <HelpBlock
        currentStepIndex={dotIndex}
        dotTooltips={dotTooltips}
        onGoBack={() => send('PREV_STEP')}
        onGoForward={() => send('NEXT_STEP')}
        onGoToStep={i => send('STEP_FROM_DOT', { dotIndex: i })}
        showFeedback={hasFeedback}
        size={size}
        title={title}
      >
        {children}
      </HelpBlock>
    </AutoScroll>
  );

  // Custom help step for docs when we want a Contact Support button
  const helpStepWithContactButton = children => (
    <AutoScroll scrollKey={scrollKey}>
      <HelpBlock
        currentStepIndex={dotIndex}
        dotTooltips={dotTooltips}
        onGoBack={() => send('PREV_STEP')}
        onGoForward={() => send('NEXT_STEP')}
        onGoToStep={i => send('STEP_FROM_DOT', { dotIndex: i })}
        showFeedback={false} // Don't show the feedback component
        size={size}
        title={title}
      >
        {children}
        {/* Add Contact Support button */}
        {hasContact && (
          <div
            style={{
              marginTop: '20px',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <LegacyButton onClick={() => send('NEXT_STEP')}>
              Contact Support
            </LegacyButton>
          </div>
        )}
      </HelpBlock>
    </AutoScroll>
  );

  switch (key) {
    case 'interstitial':
      return (
        <AutoScroll scrollKey={scrollKey}>
          <Interstitial
            hasArticles={hasDocs}
            hasCommunityConversations={hasCommunity}
            isEmailSupportEligible={isEmailSupportEligible}
            isPremierSupportEligible={isPremierSupportEligible}
            onClickBack={() => send('PREV_STEP')}
            onClickNext={() => send('NEXT_STEP')}
            onClickSkip={() => send('CONTACT')}
            size={size}
          />
        </AutoScroll>
      );

    case 'docs':
      // Use custom help step with Contact Support button when there's contact available
      // and no community (to ensure clean navigation)
      if (hasContact && !hasCommunity && skipInterstitial) {
        return helpStepWithContactButton(
          <Docs {...docs} size={size} zap={zap} />
        );
      }
      return helpStep(<Docs {...docs} size={size} zap={zap} />);

    case 'community':
      return helpStep(<CommunityStep links={community} size={size} />);

    case 'upgrade':
      return helpStep(
        <Upgrade
          onClickUpgrade={() => send('PREV_STEP')}
          viewPlansUrl={new URL('/pricing', ZAPIER_APP_BASE_URL).href}
        />
      );

    case 'contact':
      if (state === 'submitted') {
        return (
          <Fragment>
            {helpStep(<Submitted size={size} />)}
            <Spacer height={60} />
            <Separator />
            <PartnerAdditionalHelp size={size} />
          </Fragment>
        );
      }

      return helpStep(
        <Contact
          answer={answer}
          canContinue={canContinue}
          data={data}
          isSubmitting={state === 'submitting'}
          items={contact}
          setMeta={setMeta}
          submit={submit}
        />
      );

    default:
      return null;
  }
};

export default Help;
